import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/selectors/useAuth";
import useElementSize from "src/hooks/useElementSize";
import Button from "src/components/Button";
import TaskTableRow from "src/components/tasks/TaskTableRow";
import useTasks from "src/hooks/api/services/tasks/useTasks";
import { useUpdateEffectDebounce } from "src/hooks/useDebounce";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";
import List from "src/components/ui/list";
import usePermissions from "src/hooks/api/services/auth/usePermissions";
import Skeleton from "src/components/Skeleton";
import useTenant from "src/hooks/api/services/tenants/useTenant";

interface TasksTableProps {
	filters?: any;
	setModalTask: (hid: string) => void;
	searchActions?: any;
	options?: {
		scrollToTop?: boolean;
	};
}

const TasksTable = ({ setModalTask }: TasksTableProps) => {
	const { t } = useTranslation("tasks", {
		keyPrefix: "tasks-table",
	});
	const permissions = usePermissions();
	const auth = useAuth();
	const type = auth.type;
	const { ref } = useElementSize();
	const width = useRef() as any;
	const { taskType } = useTenant();
	const { tasks, status, pagination, actions } = useTasks();
	const { filter } = useTasksFilter();

	useUpdateEffectDebounce(filter, 1000, () => {
		actions.list(filter);
	});
	const loading = status === "loading" && tasks.length === 0;

	useEffect(() => {
		actions.list(
			{
				...filter,
				recent: 1,
				variant: "table",
			},
			{
				disableMergeFilter: true,
			}
		);
		return () => {
			actions.resetState();
			actions.resetFilter();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const textKey = ["both", "extern"].includes(taskType || "extern")
		? "empty.extern"
		: "empty.intern";
	const texts = {
		title:
			type === "student"
				? t(`${textKey}.student.title`)
				: t(`${textKey}.company.title`),
		text:
			type === "student"
				? t(`${textKey}.student.text`)
				: t(`${textKey}.company.text`),
	};

	if (
		tasks?.length === 0 &&
		!loading &&
		permissions.has("tasks.create") &&
		!filter
	) {
		return (
			<div className="flex flex-1 flex-col gap-4 justify-center items-center text-center">
				<h2>{t("empty.company.title")}</h2>
				<p className="text-center">{t("empty.company.text")}</p>
				<Button
					className="mt-4"
					to={
						auth.has_subscription
							? "/tasks/create"
							: "/subscription"
					}
				>
					{t("empty.company.button")}
				</Button>
			</div>
		);
	}

	return (
		<div
			{...{ ref }}
			style={{ maxWidth: width.current }}
			className="flex flex-col gap-4"
		>
			<List {...{ status }} data={tasks}>
				<List.Head>
					<List.Item>{t("headers.status")}</List.Item>
					{taskType === "extern" && auth.type === "student" && (
						<List.Item>{t("headers.company")}</List.Item>
					)}
					<List.Item>{t("headers.task")}</List.Item>
					{permissions.has("tasks.create") && (
						<List.Item>{t("headers.registrations")}</List.Item>
					)}
					<List.Item>{t("headers.budget")}</List.Item>
					<List.Item>{t("headers.date")}</List.Item>
					<List.Item>{t("headers.actions")}</List.Item>
				</List.Head>
				<List.Items>
					{(task) => (
						<TaskTableRow
							{...{ task, setModalTask }}
							key={`task-${task.hid}`}
						/>
					)}
				</List.Items>
				<List.Empty>
					<h2>{texts.title}</h2>
					<p>{texts.text}</p>
				</List.Empty>
				<List.Loading>
					<List.Item>
						<Skeleton className="h-6 w-full" />
					</List.Item>
					{taskType === "extern" && (
						<List.Item>
							<Skeleton className="h-6 w-full" />
						</List.Item>
					)}
					<List.Item>
						<Skeleton className="h-6 w-full" />
					</List.Item>
					{permissions.has("tasks.create") && (
						<List.Item>
							<Skeleton className="h-6 w-full" />
						</List.Item>
					)}
					<List.Item>
						<Skeleton className="h-6 w-full" />
					</List.Item>
					<List.Item>
						<Skeleton className="h-6 w-full" />
					</List.Item>
					<List.Item align="right">
						<Skeleton className="h-10 w-10" />
					</List.Item>
				</List.Loading>
				<List.Pagination
					{...{ pagination }}
					onNavigate={pagination.setPage}
				/>
			</List>
		</div>
	);
};

export default TasksTable;
